import { useCallback, useMemo } from "react"
import { ACTIONS, EVENTS, STATUS } from "react-joyride"

import { TOUR_STEP_FACTORIES } from "../@constants"
import {
    tourIsRunning,
    tourLanguage,
    tourLanguagePicked,
    tourStepIndex,
    tourShowLanguageSelector,
    tourDashboardIsReady,
    dashboardHasLoaded,
    resetTourSignals,
} from "../@signals"
import { isLoadingCommonFilters, disabledCommonFiltersSubmit, triggerCommonFiltersSubmit } from "@signals/filters/common"

const STORAGE_KEY = "tour-completed"
const CLOSE_DELAY = 400
const OPEN_DELAY = 1000

function closeOpenModal() {
    const closeBtn = document.querySelector('.MuiDialog-paper .MuiIconButton-root')
    if (closeBtn) { closeBtn.click(); return }
    const backdrop = document.querySelector('.MuiBackdrop-root')
    if (backdrop) backdrop.click()
}

function clickFirstRow(tableTourName) {
    const target = document.querySelector(`[data-tour="${tableTourName}"]`)
    if (!target) return
    const container = target.parentElement
    if (!container) return
    const trigger = container.querySelector("[data-tour-trigger]")
    if (trigger) trigger.click()
}

function clickByText(text) {
    const spans = document.querySelectorAll('span')
    const match = [...spans].find(el => el.textContent.trim() === text && !el.children.length)
    if (match) match.click()
}

function runTriggerSequence(sequence, index, onDone) {
    if (index >= sequence.length) { onDone(); return }
    const { selector, text, delay = 500 } = sequence[index]
    if (selector) {
        const el = document.querySelector(selector)
        if (el) el.click()
    } else if (text) {
        clickByText(text)
    }
    setTimeout(() => runTriggerSequence(sequence, index + 1, onDone), delay)
}

export const useTour = (pageKey) => {
    const steps = useMemo(
        () => TOUR_STEP_FACTORIES[pageKey]?.(tourLanguage.value) ?? [],
        // tourIsRunning.value fuerza recompute al arrancar el tour,
        // permitiendo que las factories lean signals actualizados
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pageKey, tourLanguage.value, tourIsRunning.value]
    )

    const handleStartTour = useCallback((lang) => {
        tourLanguage.value = lang
        tourLanguagePicked.value = true
        tourStepIndex.value = 0
        tourShowLanguageSelector.value = false
        tourIsRunning.value = true

        if (dashboardHasLoaded.value || disabledCommonFiltersSubmit.value) {
            // Dashboard ya tiene datos o filtros deshabilitados — arrancar tour directamente
            tourDashboardIsReady.value = true
        } else if (isLoadingCommonFilters.value) {
            // Dashboard está cargando ahora, esperar a que termine
            tourDashboardIsReady.value = false
        } else {
            // Dashboard no ha cargado, disparar búsqueda
            tourDashboardIsReady.value = false
            triggerCommonFiltersSubmit.value = triggerCommonFiltersSubmit.value + 1
        }
    }, [])

    const handleToggleLanguageSelector = useCallback(() => {
        tourShowLanguageSelector.value = !tourShowLanguageSelector.value
    }, [])

    const handleClickTourButton = useCallback(() => {
        if (tourLanguagePicked.value) { handleStartTour(tourLanguage.value); return }
        handleToggleLanguageSelector()
    }, [handleStartTour, handleToggleLanguageSelector])

    const handleJoyrideCallback = useCallback((data) => {
        const { action, index, status, type } = data

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            if (status === STATUS.FINISHED || status === STATUS.SKIPPED) return

            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1)
            const currentStep = steps[index]
            const nextStep = steps[nextIndex]

            const needsClose = !!currentStep?.data?.closeOnLeave
            const needsOpen = !!nextStep?.data?.triggerClick
            const needsTriggerSelector = !!nextStep?.data?.triggerSelector
            const needsSequence = !!nextStep?.data?.triggerSequence

            if (needsClose) closeOpenModal()

            if (needsOpen) {
                setTimeout(() => {
                    clickFirstRow(nextStep.data.triggerClick)
                    setTimeout(() => {
                        tourStepIndex.value = nextIndex
                    }, OPEN_DELAY)
                }, needsClose ? CLOSE_DELAY : 0)
                return
            }

            if (needsTriggerSelector) {
                setTimeout(() => {
                    const el = document.querySelector(nextStep.data.triggerSelector)
                    if (el) el.click()
                    setTimeout(() => {
                        tourStepIndex.value = nextIndex
                    }, OPEN_DELAY)
                }, needsClose ? CLOSE_DELAY : 0)
                return
            }

            if (needsSequence) {
                setTimeout(() => {
                    runTriggerSequence(nextStep.data.triggerSequence, 0, () => {
                        tourStepIndex.value = nextIndex
                    })
                }, needsClose ? CLOSE_DELAY : 0)
                return
            }

            if (needsClose) {
                setTimeout(() => {
                    tourStepIndex.value = nextIndex
                }, CLOSE_DELAY)
                return
            }

            tourStepIndex.value = nextIndex
        }

        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            const currentStep = steps[index]
            const needsCloseModal = !!currentStep?.data?.closeOnLeave
            resetTourSignals()
            if (needsCloseModal) setTimeout(closeOpenModal, CLOSE_DELAY)
            try {
                const completed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")
                completed[pageKey] = true
                localStorage.setItem(STORAGE_KEY, JSON.stringify(completed))
            } catch {
                // Ignore localStorage errors
            }
        }

        if (action === ACTIONS.CLOSE) {
            const currentStep = steps[index]
            const needsCloseModal = !!currentStep?.data?.closeOnLeave
            resetTourSignals()
            if (needsCloseModal) setTimeout(closeOpenModal, CLOSE_DELAY)
        }
    }, [pageKey, steps])

    return {
        steps,
        isRunning: tourIsRunning.value,
        stepIndex: tourStepIndex.value,
        showLanguageSelector: tourShowLanguageSelector.value,
        handleStartTour,
        handleToggleLanguageSelector,
        handleClickTourButton,
        handleJoyrideCallback,
    }
}
